import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';

export default function AnalyticsDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://chatpanel.my.id/api/analytics/dashboard?start_date=${dateRange.start}&end_date=${dateRange.end}`
      );
      const data = await response.json();
      
      const parsedData = {
        period: data.period,
        total_stats: Array.isArray(data.total_stats[0]) ? data.total_stats[0] : data.total_stats,
        daily_trend: Array.isArray(data.daily_trend[0]) ? data.daily_trend[0] : data.daily_trend,
        top_menus: Array.isArray(data.top_menus[0]) ? data.top_menus[0] : data.top_menus,
        top_carousels: Array.isArray(data.top_carousels[0]) ? data.top_carousels[0] : data.top_carousels,
        top_links: Array.isArray(data.top_links[0]) ? data.top_links[0] : data.top_links
      };
      
      setStats(parsedData);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSummaryValue = (eventType) => {
    if (!stats?.total_stats) return 0;
    const stat = stats.total_stats.find(s => s.event_type === eventType);
    return stat ? stat.count : 0;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        color: '#666'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '12px' }}>📊</div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in-up">
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ 
          fontSize: '36px', 
          fontWeight: '700', 
          marginBottom: '8px',
          color: '#1a1a1a'
        }}>Analytics</h1>
        <p style={{ 
          fontSize: '16px', 
          color: '#666666',
          margin: 0
        }}>Track your widget performance and user engagement.</p>
      </div>

      {/* Date Range Filter */}
      <div className="glass-card" style={{ marginBottom: '24px', padding: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
            Date Range:
          </label>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#1a1a1a'
            }}
          />
          <span style={{ color: '#666' }}>to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#1a1a1a'
            }}
          />
        </div>
      </div>

      {/* 4-Column Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', 
        gap: '20px',
        marginBottom: '32px'
      }}>
        <div className="stats-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '14px', color: 'rgba(26,26,26,0.7)', margin: 0, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Widget Opens</h3>
            <span style={{ fontSize: '24px' }}>📊</span>
          </div>
          <p style={{ fontSize: '42px', fontWeight: '700', color: '#700000', margin: '8px 0' }}>
            {getSummaryValue('widget_open')}
          </p>
          <p style={{ fontSize: '13px', color: 'rgba(26,26,26,0.6)', margin: 0 }}>Total widget interactions</p>
        </div>

        <div className="stats-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '14px', color: 'rgba(26,26,26,0.7)', margin: 0, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Menu Clicks</h3>
            <span style={{ fontSize: '24px' }}>👆</span>
          </div>
          <p style={{ fontSize: '42px', fontWeight: '700', color: '#a30021', margin: '8px 0' }}>
            {getSummaryValue('menu_click')}
          </p>
          <p style={{ fontSize: '13px', color: 'rgba(26,26,26,0.6)', margin: 0 }}>User menu selections</p>
        </div>

        <div className="stats-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '14px', color: 'rgba(26,26,26,0.7)', margin: 0, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Carousel Views</h3>
            <span style={{ fontSize: '24px' }}>👁️</span>
          </div>
          <p style={{ fontSize: '42px', fontWeight: '700', color: '#cc0033', margin: '8px 0' }}>
            {getSummaryValue('carousel_view')}
          </p>
          <p style={{ fontSize: '13px', color: 'rgba(26,26,26,0.6)', margin: 0 }}>Total carousel impressions</p>
        </div>

        <div className="stats-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '14px', color: 'rgba(26,26,26,0.7)', margin: 0, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Link Clicks</h3>
            <span style={{ fontSize: '24px' }}>🔗</span>
          </div>
          <p style={{ fontSize: '42px', fontWeight: '700', color: '#700000', margin: '8px 0' }}>
            {getSummaryValue('link_click')}
          </p>
          <p style={{ fontSize: '13px', color: 'rgba(26,26,26,0.6)', margin: 0 }}>External link engagements</p>
        </div>
      </div>

      {/* 2-Column Charts Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '20px', marginBottom: '32px' }}>
        {/* Daily Activity Trend */}
        <div className="glass-card">
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px', color: '#1a1a1a' }}>
            📈 Daily Activity Trend
          </h2>
          {stats?.daily_trend && stats.daily_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  stroke="#666"
                  style={{ fontSize: '12px' }}
                />
                <YAxis stroke="#666" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'white', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#700000" 
                  strokeWidth={2}
                  dot={{ fill: '#700000', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
              No activity data available for this period
            </div>
          )}
        </div>

        {/* Top Menus */}
        <div className="glass-card">
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px', color: '#1a1a1a' }}>
            📋 Top Menus
          </h2>
          {stats?.top_menus && stats.top_menus.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.top_menus}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="name" 
                  stroke="#666"
                  style={{ fontSize: '12px' }}
                />
                <YAxis stroke="#666" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'white', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Bar dataKey="clicks" fill="#a30021" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
              No menu data available
            </div>
          )}
        </div>

        {/* Top Carousel Content */}
        <div className="glass-card">
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px', color: '#1a1a1a' }}>
            🎠 Top Carousel Content
          </h2>
          {stats?.top_carousels && stats.top_carousels.length > 0 ? (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {stats.top_carousels.map((item, index) => (
                <div 
                  key={index} 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    marginBottom: '8px',
                    background: 'rgba(255,255,255,0.3)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
                      {item.title}
                    </p>
                    <p style={{ margin: 0, fontSize: '12px', color: 'rgba(26,26,26,0.6)' }}>
                      {item.menu_name} → {item.sub_menu_name}
                    </p>
                  </div>
                  <div style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #700000, #a30021)',
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {item.views}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
              No carousel data available
            </div>
          )}
        </div>

        {/* Most Clicked Links */}
        <div className="glass-card">
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px', color: '#1a1a1a' }}>
            🔗 Most Clicked Links
          </h2>
          {stats?.top_links && stats.top_links.length > 0 ? (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {stats.top_links.map((item, index) => (
                <div 
                  key={index} 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    marginBottom: '8px',
                    background: 'rgba(255,255,255,0.3)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
                      {item.url}
                    </p>
                  </div>
                  <div style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #700000, #a30021)',
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {item.clicks}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
              No link data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
